import React from 'react';

import { useAPI } from 'hooks/api/useAPI';

import { useInferenceClient } from './useInferenceClient';

export type ImageSearchType = 'person' | 'face';

export type InferenceDetection = {
  detection_id: string;
  score: number;
  bbox: [number, number, number, number]; // [x1, y1, x2, y2]
  embedding: number[];
};

type InferenceImage = {
  image_id: string;
  image_ref: string;
  detections: InferenceDetection[];
  errors: unknown[];
};

type InferenceResponse = {
  pipeline: string;
  model_id: string;
  detector_id: string;
  images: InferenceImage[];
  subjects: unknown[];
  errors: unknown[];
};

export type InferenceResults = {
  people: InferenceDetection[];
  faces: InferenceDetection[];
};

type UseImageInferenceReturn = {
  inferAll: (file: File) => Promise<InferenceResults>;
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
};

const MODEL_IDS: Record<ImageSearchType, string> = {
  person: 'osnet_ain',
  face: 'arcface_resnet50_w600k_r50',
};

export function useImageInference(): UseImageInferenceReturn {
  const { client, isReady } = useInferenceClient();
  const { applicationID } = useAPI();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const requestIdRef = React.useRef(0);

  const inferAll = React.useCallback(
    async (file: File): Promise<InferenceResults> => {
      if (!isReady) {
        setError('Inference service is not ready. Please try again.');
        return { people: [], faces: [] };
      }

      const currentRequestId = ++requestIdRef.current;

      setIsLoading(true);
      setError(null);

      async function inferType(
        type: ImageSearchType
      ): Promise<InferenceDetection[]> {
        const formData = new FormData();
        const detectKey = type === 'person' ? 'detect_people' : 'detect_faces';
        const request = JSON.stringify({
          model_id: MODEL_IDS[type],
          options: { [detectKey]: true, alignment_method: 'none' },
        });

        formData.append('request', request);
        formData.append('files', file);

        const { data } = await client.post<InferenceResponse>(
          `/apps/${applicationID}/infer/pipelines/reid/${type}`,
          formData,
          { timeout: 120000 }
        );

        return data.images?.[0]?.detections ?? [];
      }

      const [peopleResult, facesResult] = await Promise.allSettled([
        inferType('person'),
        inferType('face'),
      ]);

      if (currentRequestId === requestIdRef.current) {
        const errors: string[] = [];
        if (peopleResult.status === 'rejected') {
          errors.push(`Person detection failed: ${peopleResult.reason}`);
        }
        if (facesResult.status === 'rejected') {
          errors.push(`Face detection failed: ${facesResult.reason}`);
        }
        if (errors.length > 0) {
          setError(errors.join('. '));
        }
        setIsLoading(false);
      }

      return {
        people:
          peopleResult.status === 'fulfilled' ? peopleResult.value : [],
        faces:
          facesResult.status === 'fulfilled' ? facesResult.value : [],
      };
    },
    [client, applicationID, isReady]
  );

  return { inferAll, isReady, isLoading, error };
}
